import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Pricing from "../components/Pricing";
import FAQ from "../components/FAQ";
import GoogleReviews from "../components/GoogleReviews";
import VideoTestimonials from "../components/VideoTestimonials";
import Industries from "../components/Industries";
import { usePageMeta, ORG_JSON_LD, productJsonLd } from "../lib/usePageMeta";
import {
  CheckCircle2,
  Clock,
  Shield,
  Users,
  Sparkles,
  Star,
  FileText,
  PencilLine,
  MessageCircleQuestion,
  ArrowRight,
} from "lucide-react";

const META = {
  seo: {
    title:
      "CV Writing Service · Professional CV Writers UK · CV Folks",
    description:
      "CV Writing Service by professional CV writers. CV review and rewrite, CV editing service and resume writer help from £50. 60-day interview guarantee · 24–48h turnaround.",
    keywords:
      "cv writing service, professional cv writer, professional cv writers, cv writer, cv company, cv review and rewrite, resume writer, best cv writing service uk, cv editing service, cv help",
  },
  jsonLd: [
    ORG_JSON_LD,
    productJsonLd({
      id: "standard-cv",
      name: "CV Writing Service — Professional CV Writer (Standard)",
      price: 50,
      description:
        "Professional CV writing service by UK CV writers. CV review, rewrite and ATS-friendly formatting.",
    }),
    productJsonLd({
      id: "premium-package",
      name: "CV Writing Service — Premium Package with Professional CV Writers",
      price: 155,
      description:
        "Premium CV, cover letter, LinkedIn makeover and consultation call by expert UK CV writers.",
    }),
  ],
};

/**
 * Dynamic Keyword Insertion (DKI) — Google Ads final URL support.
 *
 * Usage: append `?kw=cv+writer` to your Ads final URLs and this page will
 * swap the hero H1 lead phrase + eyebrow + sub-copy to match. Only the
 * whitelisted keywords below are honoured (prevents XSS / weird copy).
 * Any unknown / missing `kw` falls back to the default hero.
 *
 * Both required phrases ("CV Writing Service" or "Professional CV Writer")
 * always appear in the H1 across every variant.
 */
const DKI_VARIANTS = {
  "cv writer": {
    lead: "CV Writer",
    accent: "· Professional CV Writing Service",
    eyebrow: "UK's trusted CV writer · Since 2019",
    sub: "Hire a CV writer who knows your sector. Every CV is hand-written by a professional CV writer — from £50 with a 60-day interview guarantee.",
    cta: "Hire my CV writer — from £50",
  },
  "professional cv writers": {
    lead: "Professional CV Writers",
    accent: "· UK CV Writing Service",
    eyebrow: "Professional CV writers · London & UK",
    sub: "Work directly with a professional CV writer in the UK. Full CV writing service, CV rewrite, editing and cover letters — from £50 with a 60-day interview guarantee.",
    cta: "Work with a professional CV writer — £50",
  },
  "professional cv writer": {
    lead: "Professional CV Writer",
    accent: "· Trusted UK CV Writing Service",
    eyebrow: "Professional CV writer · London & UK",
    sub: "One professional CV writer, matched to your sector. CV writing, rewrite and editing service — from £50 with a 60-day interview guarantee.",
    cta: "Get matched with a CV writer — £50",
  },
  "cv company": {
    lead: "CV Company",
    accent: "· Professional CV Writers UK",
    eyebrow: "The UK's trusted CV company · Since 2019",
    sub: "A CV company built around professional CV writers, not templates. Full CV writing service, CV review and editing — from £50 with a 60-day interview guarantee.",
    cta: "Start with our CV company — £50",
  },
  "cv writing service": {
    lead: "CV Writing Service",
    accent: "by Professional CV Writers",
    eyebrow: "CV Company · UK's trusted CV writing service",
    sub: "Get a recruiter-focused CV designed to win more interviews. Our professional CV writers provide expert CV writing, CV review & rewrite and resume editing service from £50, backed by our 60-Day Interview Guarantee.",
    cta: "Get my new CV — from £50",
  },
  "cv review and rewrite": {
    lead: "CV Review and Rewrite",
    accent: "by Professional CV Writers",
    eyebrow: "CV review · rewrite · editing · UK",
    sub: "Send us your existing CV — a professional CV writer will review, rewrite and tighten every section into an interview-winning document. From £50 with a 60-day interview guarantee.",
    cta: "Get my CV reviewed & rewritten — £50",
  },
  "resume writer": {
    lead: "Resume Writer",
    accent: "· Professional CV Writing Service",
    eyebrow: "Resume writer · UK-based · Since 2019",
    sub: "Need a resume, not a CV? Our professional resume writers craft the right length, format and tone for UK and international roles — from £50 with a 60-day interview guarantee.",
    cta: "Get my resume written — from £50",
  },
  "best cv writing service uk": {
    lead: "The Best CV Writing Service in the UK",
    accent: "· Professional CV Writers",
    eyebrow: "Rated 4.9★ · UK's best CV writing service",
    sub: "Voted the best CV writing service in the UK — hand-written by professional CV writers, sector-tuned and backed by a 60-day interview guarantee. From £50.",
    cta: "Start with the UK's best — £50",
  },
  "cv editing service": {
    lead: "CV Editing Service",
    accent: "by Professional CV Writers",
    eyebrow: "CV editing · rewrite · UK CV writers",
    sub: "Line-by-line CV editing service by a professional CV writer — tone, clarity, structure and ATS keywords tuned to your target role. From £50 with a 60-day interview guarantee.",
    cta: "Get my CV edited — from £50",
  },
  "cv help": {
    lead: "CV Help",
    accent: "· Professional CV Writers UK",
    eyebrow: "CV help · UK-based CV writers · Since 2019",
    sub: "Real CV help from a real professional CV writer — CV rewrite, editing, cover letters and 1:1 advice on how to position your experience. From £50, 60-day interview guarantee.",
    cta: "Get CV help — from £50",
  },
};

const DEFAULT_HERO = DKI_VARIANTS["cv writing service"];

// Normalise incoming kw (e.g. "cv+writer", "CV Writer", "cv%20writer") to a
// lowercase whitelist key. Returns null if not whitelisted.
function normaliseKw(raw) {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace(/\+/g, " ")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return Object.prototype.hasOwnProperty.call(DKI_VARIANTS, cleaned)
    ? cleaned
    : null;
}

const BENEFITS = [
  {
    icon: Users,
    title: "UK-based CV writers",
    text: "Every CV is hand-written by a professional CV writer who knows your sector.",
  },
  {
    icon: Clock,
    title: "24–48 hour turnaround",
    text: "First draft in your inbox within two business days, often sooner.",
  },
  {
    icon: Shield,
    title: "60-day interview guarantee",
    text: "No interview in 60 days? We rewrite your CV for free and give you extra resources and support to help you land interviews faster.",
  },
  {
    icon: CheckCircle2,
    title: "ATS-friendly by design",
    text: "Structure and keywords that pass Applicant Tracking Systems.",
  },
];

const WHAT_YOU_GET = [
  {
    icon: FileText,
    title: "CV Writing Service",
    text: "A brand new, tailored CV written from scratch by a professional CV writer for your target role.",
  },
  {
    icon: PencilLine,
    title: "CV Review and Rewrite",
    text: "Send us your current CV — we rewrite it into an interview-winning document, keeping what works.",
  },
  {
    icon: Sparkles,
    title: "CV Editing Service",
    text: "Line-by-line CV editing for tone, clarity, structure and ATS keywords by our UK CV writers.",
  },
  {
    icon: FileText,
    title: "Resume Writer",
    text: "Need a resume instead of a CV? Our writers craft resumes with the right length, format and voice.",
  },
  {
    icon: MessageCircleQuestion,
    title: "CV Help & Consultation",
    text: "Personal CV help and 1:1 advice on how to position your experience for the roles you actually want.",
  },
  {
    icon: Shield,
    title: "Unlimited Revisions",
    text: "We keep refining your CV until you're happy — no extra fees, no time limits during the review window.",
  },
];

const PROCESS = [
  {
    step: "01",
    title: "Choose your package",
    text: "Pick the CV writing service package that suits your career goals.",
  },
  {
    step: "02",
    title: "Complete the questionnaire",
    text: "Share your target role and upload your existing CV if you have one.",
  },
  {
    step: "03",
    title: "Matched with a CV writer",
    text: "You're paired with a professional CV writer experienced in your sector.",
  },
  {
    step: "04",
    title: "Personalised CV written",
    text: "We write a bespoke, ATS-friendly CV tailored to your target role.",
  },
  {
    step: "05",
    title: "Review & revisions",
    text: "Review your draft, request edits and start applying with confidence.",
  },
];

const WHY_BEST = [
  {
    title: "Written by humans, not templates",
    text: "Every CV is written by a real professional CV writer in the UK — no AI-only output, no generic templates.",
  },
  {
    title: "Sector-specific CV help",
    text: "Finance, tech, healthcare, executive, graduate — our CV writers match your industry, not just your job title.",
  },
  {
    title: "60-day interview guarantee",
    text: "If your rewritten CV doesn't land you an interview in 60 days, we rewrite it for free AND provide extra resources and support to help you land interviews faster. That's the best CV writing service promise in the UK.",
  },
];

// Reusable CTA that scrolls to the pricing block
function goToPricing(id) {
  window.dispatchEvent(new CustomEvent("pricing:goto", { detail: { id } }));
}

export default function ServicesCvWritingPage() {
  usePageMeta(META);
  const [searchParams] = useSearchParams();
  const hero = useMemo(() => {
    const kw = normaliseKw(searchParams.get("kw"));
    return kw ? DKI_VARIANTS[kw] : DEFAULT_HERO;
  }, [searchParams]);

  return (
    <>
      <Navbar />
      <main
        data-testid="services-cv-writing-page"
        className="pt-24 md:pt-32 bg-background"
      >
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-14 md:pb-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="label-eyebrow" data-testid="services-hero-eyebrow">
              {hero.eyebrow}
            </p>
            <h1
              data-testid="services-hero-h1"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05]"
            >
              <span className="text-brand">{hero.lead}</span> {hero.accent}
            </h1>
            <p className="text-base md:text-lg text-foreground/70 mt-5">
              {hero.sub}
            </p>

            {/* Trust chips above the fold */}
            <div
              data-testid="services-hero-trust"
              className="mt-6 flex flex-wrap gap-2 text-xs"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 text-brand px-3 py-1.5 font-semibold">
                <Star className="h-3.5 w-3.5 fill-brand" /> 4.9★ Google Rated
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold text-foreground/80">
                <Shield className="h-3.5 w-3.5" /> 60-day interview guarantee
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold text-foreground/80">
                <Clock className="h-3.5 w-3.5" /> 24–48h turnaround
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold text-foreground/80">
                From £50
              </span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => goToPricing("standard-cv")}
                data-testid="services-cta-standard"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-base font-semibold hover:opacity-90 shadow-sm transition"
              >
                {hero.cta} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="relative rounded-3xl border border-border bg-card p-6 md:p-8 shadow-sm">
            <p className="label-eyebrow">Why customers choose our CV writers</p>
            <div className="mt-4 space-y-4">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex gap-3">
                  <b.icon className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">{b.title}</p>
                    <p className="text-sm text-foreground/70">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust band directly under hero */}
        <section
          data-testid="services-trust-band"
          className="border-y border-border bg-muted/40"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-sm text-foreground/70">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <Star className="h-4 w-4 fill-brand text-brand" /> 4.9★ on Google
            </span>
            <span>·</span>
            <span>1,200+ UK professionals hired</span>
            <span>·</span>
            <span>Featured across LinkedIn, Reed & Indeed profiles</span>
            <span>·</span>
            <span>Trusted CV company since 2019</span>
          </div>
        </section>

        <Pricing />

        <VideoTestimonials />

        {/* Our Professional CV Writing Process — 5 steps */}
        <section
          data-testid="services-process-section"
          className="bg-muted/30 py-20 md:py-28"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="max-w-2xl mx-auto text-center">
              <p className="label-eyebrow">How our CV writing service works</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
                Five clear steps from{" "}
                <span className="text-brand">brief to interview-ready CV.</span>
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground/70">
                Every professional CV writer follows the same proven process — so you always
                know what happens next.
              </p>
            </div>

            <div className="mt-14 grid md:grid-cols-3 lg:grid-cols-5 gap-5">
              {PROCESS.map((p, i) => (
                <article
                  key={p.step}
                  data-testid={`services-process-${p.step}`}
                  className="group relative bg-card border border-border rounded-2xl p-6 hover:border-brand/50 hover:shadow-xl hover:-translate-y-0.5 transition"
                >
                  <span className="absolute -top-3 -left-3 h-9 w-9 rounded-full bg-brand text-white flex items-center justify-center text-xs font-bold shadow-md group-hover:scale-110 transition">
                    {i + 1}
                  </span>
                  <p className="text-2xl font-serif text-brand/40 font-bold">{p.step}</p>
                  <h3 className="font-serif text-lg font-bold mt-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">
                    {p.text}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                onClick={() => goToPricing("standard-cv")}
                data-testid="services-process-cta-standard"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:opacity-90 shadow-sm transition"
              >
                Start with a CV writer — from £50
              </button>
              <button
                type="button"
                onClick={() => goToPricing("premium-cv")}
                data-testid="services-process-cta-premium"
                className="inline-flex items-center gap-2 rounded-full border border-foreground px-6 py-3.5 text-sm font-semibold hover:bg-foreground hover:text-background transition"
              >
                Premium CV · £125
              </button>
            </div>
          </div>
        </section>

        {/* What you get — moved below the process section per request */}
        <section
          data-testid="services-whatyouget"
          className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24"
        >
          <div className="max-w-2xl">
            <p className="label-eyebrow">What you get with our CV writing service</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 leading-[1.1]">
              One professional CV writer.{" "}
              <span className="text-brand">Everything your CV needs.</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground/70">
              Whether you need a full rewrite, a quick CV edit or just some CV help &mdash; every
              package is delivered by a UK-based professional CV writer.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_YOU_GET.map((item) => (
              <article
                key={item.title}
                data-testid={`services-whatyouget-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl border border-border bg-card p-6 hover:border-brand/40 hover:shadow-md transition"
              >
                <div className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif text-lg font-bold mt-4">{item.title}</h3>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-10">
            <button
              type="button"
              onClick={() => goToPricing("standard-cv")}
              data-testid="services-whatyouget-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:opacity-90 shadow-sm transition"
            >
              Choose my CV writer — from £50 <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* Best CV Writing Service UK — differentiators */}
        <section
          data-testid="services-why-best"
          className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24"
        >
          <div className="max-w-2xl">
            <p className="label-eyebrow">Best CV Writing Service in the UK</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 leading-[1.1]">
              Why CV Folks is voted the{" "}
              <span className="text-brand">best CV writing service in the UK</span>.
            </h2>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {WHY_BEST.map((b, i) => (
              <div
                key={b.title}
                data-testid={`services-why-best-${i}`}
                className="rounded-2xl border border-border p-6 bg-card"
              >
                <p className="text-xs font-bold text-brand">0{i + 1}</p>
                <h3 className="font-serif text-lg font-bold mt-2">{b.title}</h3>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        <GoogleReviews />

        <Industries />

        <FAQ />

        {/* Bottom CTA */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 pb-28 md:pb-24 pt-4">
          <div className="rounded-3xl bg-foreground text-background p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold">
              Ready to work with a professional CV writer?
            </h2>
            <p className="text-base text-background/70 mt-3 max-w-xl mx-auto">
              Pick the CV writing service package that suits you &mdash; we&apos;ll match you with the
              right CV writer for your sector, usually within a few hours.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                onClick={() => goToPricing("standard-cv")}
                data-testid="services-bottom-cta-standard"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                Standard from £50
              </button>
              <button
                type="button"
                onClick={() => goToPricing("premium-cv")}
                data-testid="services-bottom-cta-premium"
                className="inline-flex items-center gap-2 rounded-full border border-background/40 px-6 py-3 text-sm font-semibold hover:bg-background/10 transition"
              >
                Premium CV · £125
              </button>
            </div>
          </div>
        </section>

        {/* Sticky mobile CTA (mobile only) — improves mobile Ads landing page CTR */}
        <div
          data-testid="services-mobile-sticky-cta"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
        >
          <div className="text-xs">
            <p className="font-bold leading-tight">Professional CV Writer</p>
            <p className="text-foreground/60 leading-tight">From £50 · 60-day guarantee</p>
          </div>
          <button
            type="button"
            onClick={() => goToPricing("standard-cv")}
            data-testid="services-mobile-sticky-cta-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold shadow-sm active:scale-[0.98] transition"
          >
            Get started <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </main>
      <Footer />
    </>
  );
}
